import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import type { RetrieveClaimWithRelationsResponse } from "@upclaim/sdk/src/data-contracts";

import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

export type ClaimWithRelations = RetrieveClaimWithRelationsResponse;

/** Claim with all relations — mirrors ClaimStore.retrieveClaim (Vue). */
export function useClaim(claimId: string) {
  const queryClient = useQueryClient();

  // biome-ignore lint/correctness/useExhaustiveDependencies: claimId is only a change trigger, not read in the body
  useEffect(() => {
    // The backend records the view as a side effect of this GET, and this hook is the
    // single entry point for it (full claim page *and* the /claims preview drawer).
    // Drop the cached recents so the palette re-fetches on its next open.
    queryClient.invalidateQueries({ queryKey: queryKeys.claims.recentlyViewed });
  }, [claimId, queryClient]);

  return useQuery({
    queryKey: queryKeys.claims.detail(claimId),
    queryFn: () => adminAPI.retrieveClaimWithAllRelations(claimId),
  });
}

/** Feature flag keys enabled for a claim — mirrors ClaimStore.featureFlags. */
/** Whether the claim's travel agency asks the passenger questionnaire, and its default blocks (TECH-1352). */
export function useClaimPassengerQuestionnaire(claimId: string) {
  return useQuery({
    queryKey: queryKeys.claims.passengerQuestionnaire(claimId),
    queryFn: () => adminAPI.getClaimPassengerQuestionnaire(claimId),
  });
}

export function useClaimFeatureFlags(claimId: string) {
  return useQuery({
    queryKey: queryKeys.claims.featureFlags(claimId),
    queryFn: async () => {
      const { keys } = await adminAPI.getClaimFeatureFlags(claimId);
      return keys;
    },
  });
}
